// What the simulator remembers that the live period does not produce: the
// details behind each finished period's team total (bench points, the
// optimal lineup, the top starter, the projection) and the league's past
// seasons, both generated deterministically from the seed so a trophy room
// has depth on day one. The real API stores these as they happen; the
// shapes here are the awards engine's facts.

import type { PastSeasonFact, PastSeasonTeam } from '@fsp/awards';
import { SEASON_AWARDS, WEEKLY_AWARDS } from '@fsp/awards';
import type { SimPlayer, SimTeam } from './league';
import { detailOf, pastWeek, type LineupRows } from './past-weeks';
import { hashSeed, rng } from './rng';

export interface PeriodTeamDetail {
  bench: number;
  /** The best lineup the roster could have started; never below the total. */
  optimal: number;
  topPlayerId: string | null;
  topPoints: number;
  projected: number;
  emptyStarters: number;
  inactiveStarters: number;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Details for every finished period, from the players' own points that week
 * in the lineup each team was made with (`pastWeek`, B-527): its bench, the
 * best lineup it could have started, its top starter and its projection.
 * They were drawn for the team until 2026-10-26, beside a score with nobody's
 * points behind it. Nobody was inactive in a week gone by: the tour keeps no
 * injury report for one.
 */
export function buildPeriodDetails(
  teams: readonly SimTeam[],
  players: ReadonlyMap<string, SimPlayer>,
  lineups: ReadonlyMap<string, LineupRows>,
  finals: ReadonlyMap<string, number>,
  lastPeriod: number,
): Map<string, PeriodTeamDetail> {
  const out = new Map<string, PeriodTeamDetail>();
  for (let period = 1; period <= lastPeriod; period++) {
    for (const team of teams) {
      if (!finals.has(`${period}:${team.id}`)) continue;
      const lineup = lineups.get(`${period}:${team.id}`) ?? team.lineup;
      out.set(`${period}:${team.id}`, detailOf(pastWeek(lineup, players, period)));
    }
  }
  return out;
}

/**
 * Past seasons for an established league. Champions can be named by owner
 * (the demo's story: Ken took 2024, Dave 2025); everything else is drawn.
 */
export function buildHistory(
  seed: number,
  teams: readonly SimTeam[],
  season: number,
  count: number,
  champions: readonly (string | null)[] = [],
  regularSeasonGames = 14,
  weeks = 17,
): PastSeasonFact[] {
  const out: PastSeasonFact[] = [];
  const seated = teams.filter((t) => t.ownerName);
  if (seated.length < 2) return out;
  const n = seated.length;
  const playoffTeams = n >= 10 ? 6 : n >= 6 ? 4 : 2;
  for (let i = 0; i < count; i++) {
    const year = season - count + i;
    const r = rng(hashSeed(seed, 'history', year));
    const order = r.shuffle(seated);
    const wantChampion = champions[i] ?? null;
    if (wantChampion) {
      const idx = order.findIndex((t) => t.ownerName === wantChampion);
      if (idx > 0) {
        // The champion finishes in the top three of the regular season.
        const spot = r.int(0, Math.min(2, n - 1));
        const [team] = order.splice(idx, 1);
        order.splice(spot, 0, team!);
      }
    }
    const championTeam =
      order.find((t) => t.ownerName === wantChampion) ?? order[r.int(0, Math.min(3, n - 1))]!;
    const finalists = order.slice(0, playoffTeams).filter((t) => t !== championTeam);
    const runnerUp = r.pick(finalists);
    const rows: PastSeasonTeam[] = order.map((team, index) => {
      const rank = index + 1;
      const wins = Math.max(
        1,
        Math.min(
          regularSeasonGames - 1,
          Math.round(regularSeasonGames * (1 - (rank - 0.5) / n) + r.around(0, 1.2)),
        ),
      );
      return {
        teamId: team.id,
        ownerName: team.ownerName,
        wins,
        losses: regularSeasonGames - wins,
        ties: 0,
        pointsFor: round1(r.around(1560 - (rank - 1) * 28, 55)),
        pointsAgainst: round1(r.around(1500, 55)),
        finish: rank,
        of: n,
        playoffs: rank <= playoffTeams,
        champion: team === championTeam,
        runnerUp: team === runnerUp,
        weeklyAwards: {},
        seasonAwards: [],
        bestWeek: round1(r.around(146 - (rank - 1) * 2.5, 10, 105, 195)),
        longestStreak: Math.max(1, Math.round(r.around(4.5 - rank * 0.25, 1.4, 1, 10))),
        biggestMargin: round1(r.around(38 - rank, 12, 8, 90)),
        tradesCompleted: r.int(0, 4),
        claimsWon: r.int(1, 12),
        perfectWeeks: r.int(0, 4),
        ironStreak: r.int(2, regularSeasonGames),
        giantKills: r.int(0, 4),
        escapes: r.int(0, 3),
        vs: {},
      };
    });
    // Head-to-head: every pair met once, some twice; the better finish is favored.
    for (let i = 0; i < rows.length; i++)
      for (let j = i + 1; j < rows.length; j++) {
        const a = rows[i]!;
        const b = rows[j]!;
        const meetings = r.chance(3 / Math.max(3, n - 1)) ? 2 : 1;
        let aw = 0;
        let bw = 0;
        let last = 0;
        for (let g = 0; g < meetings; g++) {
          const pA = Math.min(0.85, Math.max(0.15, 0.5 + (b.finish - a.finish) * 0.04));
          if (r.chance(pA)) {
            aw++;
            last = last > 0 ? last + 1 : 1;
          } else {
            bw++;
            last = last < 0 ? last - 1 : -1;
          }
        }
        a.vs![b.teamId] = { wins: aw, losses: bw, ties: 0, streak: last };
        b.vs![a.teamId] = { wins: bw, losses: aw, ties: 0, streak: -last };
      }
    // Weekly awards: glory leans to the top, shame to the bottom, the rest is luck.
    for (let week = 1; week <= weeks; week++)
      for (const def of WEEKLY_AWARDS) {
        const weight = (index: number) =>
          def.tone === 'glory' ? n - index : def.tone === 'shame' ? index + 1 : 1;
        const total = rows.reduce((s, _, idx) => s + weight(idx), 0);
        let pick = r.next() * total;
        let chosen = rows[0]!;
        for (let idx = 0; idx < rows.length; idx++) {
          pick -= weight(idx);
          if (pick <= 0) {
            chosen = rows[idx]!;
            break;
          }
        }
        chosen.weeklyAwards[def.id] = (chosen.weeklyAwards[def.id] ?? 0) + 1;
      }
    // Season awards: one winner each.
    const give = (awardId: string, team: PastSeasonTeam | undefined) => {
      if (team) team.seasonAwards.push(awardId);
    };
    const byId = new Map(SEASON_AWARDS.map((d) => [d.id, d]));
    give(
      'champion',
      rows.find((t) => t.champion),
    );
    give(
      'runner-up',
      rows.find((t) => t.runnerUp),
    );
    give('points-title', [...rows].sort((a, b) => b.pointsFor - a.pointsFor)[0]);
    give('last-place', rows[rows.length - 1]);
    give('week-of-the-year', [...rows].sort((a, b) => b.bestWeek - a.bestWeek)[0]);
    give('juggernaut', [...rows].sort((a, b) => b.longestStreak - a.longestStreak)[0]);
    give('punching-bag', [...rows].sort((a, b) => b.pointsAgainst - a.pointsAgainst)[0]);
    for (const id of ['comeback-kid', 'benchwarmer', 'waiver-wizard', 'trade-shark'])
      if (byId.has(id)) give(id, r.pick(rows));
    out.push({ season: year, teams: rows });
  }
  return out;
}
